import { Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { CustomerProductMbbState, CustomerProductsMbbStateModel } from '../../modules/mbb/states/customer-product-mbb.state';
import {
  CustomerProductHardwareState,
  CustomerProductsAccessoryStateModel,
} from '../accessory-product/accessory-product.state';
import { Product } from '@shared/models/product.model';
import { MbbDeliveryOptions } from '../../modules/mbb/models/mbb-delivery-options.model';
import { CustomerProductMbbSelectors } from '@sales/modules/mbb/states/customer-product-mbb.selectors';
import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import { PaymentType } from '@shared/enums/payment-type';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { CustomerProductMobilePhoneSelectors } from '../customer-product-mobilephone/customer-product-mobilephone.selectors';
import { CustomerProductsAccessorySelectors } from '../accessory-product/accessory-product.selectors';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { UpfrontPaymentProducts } from '@sales/models/upfront-payment-products';

@Injectable()
export class PaymentSelectors {
  // productsWithUpfrontPayment returns products that Diego/ice will generate a payment link for
  // i.e. user will receive an sms and pay through it
  @Selector([
    CustomerProductMbbSelectors.products,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
  ])
  public static productsWithUpfrontPayment(
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[],
    mobilePhoneProducts: MobilePhoneProduct[]
  ): UpfrontPaymentProducts {
    const mbbProductsWithUpfrontPayment: MbbProduct[] = mbbProducts.filter(
      product => product.paymentType === PaymentType.Upfront
    );
    const accessoriesWithUpfrontPayment: HardwareProduct[] = accessoryProducts.filter(
      product => product.paymentType === PaymentType.Upfront
    );
    const mobilePhoneProductsWithUpfrontPayment: MobilePhoneProduct[] = mobilePhoneProducts.filter(
      product =>
        (product.paymentType === MobilePaymentType.PayNowWithSubscription ||
          product.paymentType === MobilePaymentType.PayNowWithoutSubscription) &&
        product.sendInPost
    );

    return {
      mbbProducts: mbbProductsWithUpfrontPayment,
      accessoryProducts: accessoriesWithUpfrontPayment,
      mobilePhoneProducts: mobilePhoneProductsWithUpfrontPayment,
      amount:
        mbbProductsWithUpfrontPayment.length +
        accessoriesWithUpfrontPayment.length +
        mobilePhoneProductsWithUpfrontPayment.length,
    };
  }

  // productsToBePaidNowByCustomer returns all products that will be fully paid by the customer,
  // which includes products with upfront payment and also the ones that customer will pay directly at the shop
  @Selector([
    CustomerProductMbbSelectors.products,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
  ])
  public static productsToBePaidNowByCustomer(
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[],
    mobilePhoneProducts: MobilePhoneProduct[]
  ): UpfrontPaymentProducts {
    const mbbProductsToPayNow: MbbProduct[] = mbbProducts.filter(
      product =>
        !!product.mbbRouter?.offeringId &&
        product.deliveryOption !== MbbDeliveryOptions.AlreadyOwnRouter &&
        product.paymentType !== PaymentType.PayWithNextInvoice
    );
    const accessoriesToPayNow: HardwareProduct[] = accessoryProducts.filter(
      product => product.paymentType !== PaymentType.PayWithNextInvoice
    );
    const mobilePhoneProductsToPayNow: MobilePhoneProduct[] = mobilePhoneProducts.filter(
      product =>
        product.paymentType === MobilePaymentType.PayNowWithSubscription ||
        product.paymentType === MobilePaymentType.PayNowWithoutSubscription
    );

    return {
      mbbProducts: mbbProductsToPayNow,
      accessoryProducts: accessoriesToPayNow,
      mobilePhoneProducts: mobilePhoneProductsToPayNow,
      amount: mbbProductsToPayNow.length + accessoriesToPayNow.length + mobilePhoneProductsToPayNow.length,
    };
  }

  @Selector([CustomerProductMbbState, CustomerProductHardwareState])
  static productsWithoutPayment(
    mbbState: CustomerProductsMbbStateModel,
    accessoryState: CustomerProductsAccessoryStateModel
  ): Product[] {
    const mbbProducts = mbbState.products
      .filter(product => product.deliveryOption === MbbDeliveryOptions.SendRouterInPost)
      .filter(x => !x.paymentType);

    const hardwareProducts = accessoryState.products.filter(x => !x.paymentType);

    return [...mbbProducts, ...hardwareProducts];
  }
}
